// The SO-Finalize email (components/SoFinalizeEmailDialog.jsx; POST /packaging/plans/{id}/finalize-so-pickup and
// /finalize-so-shipment). jobq-api (2026-10-27, `_so_finalize_email_overrides`): the standard notice to the order's
// own contacts goes on the typed operator PIN; an EDITED subject or body (anything but the standard copy the
// preview handed the dialog), or a recipient not on file, needs the email Admin PIN on the request (X-Admin-Pin,
// the same check as every outward email: utils/emailSender.js). The dialog only offers recipients on file (the
// customer's active contacts, or customers.email as the "customer default"), so only an edited text can need it.
import config from '../../config.js';
import { detailToString } from '../../utils/apiError';
import { emailSenderHeaders } from '../../utils/emailSender';

const API_BASE = config.API_BASE_URL;

/** Subject / body as jobq-api compares them with the standard copy (_norm_email_text). */
export function normEmailText(s) {
  return String(s == null ? '' : s).replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
}

/** Did the operator change the subject or the body from what the preview gave? (preview: {subject, body_text}) */
export function finalizeEmailEdited(preview, subject, body) {
  if (!preview) return false;
  return normEmailText(subject) !== normEmailText(preview.subject)
    || normEmailText(body) !== normEmailText(preview.body_text);
}

/**
 * Headers for a finalize send. A standard send (nothing edited, or no email at all): {} (the worker PIN in the body
 * is enough). An edited email: the admin session's X-Admin-Pin; with no admin session, askAdminPin() is asked for one
 * (resolves a PIN or null). Resolves {headers} or {cancelled: true} when an Admin PIN was needed and none was given.
 */
export async function finalizeEmailHeaders({ notify, edited, askAdminPin }) {
  if (!notify || !edited) return { headers: {} };
  const fromSession = emailSenderHeaders();
  if (fromSession['X-Admin-Pin']) return { headers: fromSession };
  const pin = askAdminPin ? await askAdminPin() : null;
  if (!pin) return { cancelled: true };
  return { headers: { 'X-Admin-Pin': pin } };
}

export const EDITED_EMAIL_NOTE = 'You changed the subject or message: sending it needs an Admin PIN. '
  + 'Use the standard text to send on your own PIN.';

// --- The dialog's reads and send: ONE copy for the desktop dialog (components/SoFinalizeEmailDialog.jsx) and the phone
// package guide's finalize sheet (mobile/screens/shipping/FinalizeSheet.jsx). Moved from the dialog word for word.

/** GET /packaging/plans/{id}/customer-email-preview: the recipients on file, the standard subject / body, and what the
 *  finalize would email. Throws with the API's sentence. */
export async function loadFinalizePreview(planId, apiBase = API_BASE) {
  const r = await fetch(`${apiBase}/packaging/plans/${planId}/customer-email-preview`);
  if (!r.ok) {
    const b = await r.json().catch(() => ({}));
    throw new Error(detailToString(b.detail) || `HTTP ${r.status}`);
  }
  return r.json();
}

/** The finalize endpoint for a kind ('pickup' | anything else = shipping). */
export function finalizeEndpoint(planId, kind) {
  return kind === 'pickup'
    ? `/packaging/plans/${planId}/finalize-so-pickup`
    : `/packaging/plans/${planId}/finalize-so-shipment`;
}

/** What the preview says about this finalize (null preview = still loading):
 *  emailScope 'none' = every pallet it covers was already emailed (finalizing records the close-out, no second email);
 *  'partial' = only the pallets not yet emailed go out; finalizeBlocked = finalized already and no new batch. */
export function finalizePreviewState(preview) {
  const contacts = preview?.contacts || [];
  const customerFallback = preview?.customer_email_fallback || null;
  const emailScope = preview?.email_scope || 'all';
  const finalizeBlocked = !!preview?.already_finalized && !preview?.batch_available;
  return {
    contacts,
    customerFallback,
    hasAnyRecipientOption: contacts.length > 0 || !!customerFallback,
    emailScope,
    finalizeBlocked,
    showNoEmail: !!preview && !finalizeBlocked && emailScope === 'none',
    pendingPallets: preview?.email_pending_pallets || [],
    notifiedSummary: preview?.email_notified_summary || '',
  };
}

/** Why the send can't go yet (the sentence to show), or null. */
export function finalizeProblem({ pin, notify, hasAnyRecipientOption, selectedToId }) {
  if (!pin) return 'Enter your 4-digit PIN to finalize.';
  if (notify && !hasAnyRecipientOption) return 'No recipient on file. Click "Finalize without email" to skip the customer email.';
  if (notify && !selectedToId) return 'Pick a recipient (or click "Finalize without email" to skip).';
  return null;
}

/** The send's body. A picked contact sends to_contact_id; the customer.email fallback sends to_email. */
export function finalizePayload({ pin, notify, selectedToId, customerFallback, ccContactIds, sendCopyToSelf, subject, body }) {
  const payload = { pin, notify_customer: notify };
  if (notify) {
    if (selectedToId === '__customer_default__' && customerFallback) payload.to_email = customerFallback.email;
    else if (selectedToId) payload.to_contact_id = selectedToId;
    payload.cc_contact_ids = ccContactIds;
    payload.send_copy_to_self = sendCopyToSelf;
    payload.custom_subject = subject;
    payload.custom_body = body;
  }
  return payload;
}

/** POST the finalize (headers from finalizeEmailHeaders). Resolves the API's answer; throws with its sentence. */
export async function sendFinalize({ planId, kind, payload, headers = {}, apiBase = API_BASE }) {
  const res = await fetch(`${apiBase}${finalizeEndpoint(planId, kind)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
  }
  return res.json();
}
